// Anticipated warm-up of the FIRST reel, fired from the Home screen (idle) so that
// when the member taps "Reels" the very first video starts TikTok-instantly:
//   • the reels list request warms the API cache,
//   • TCP+TLS to the video CDN is pre-opened,
//   • the HLS manifest + lowest-rendition media playlist + FIRST segment land in the
//     browser HTTP cache (hls.js re-requests them instantly from cache),
//   • the poster image is pre-decoded (the full-screen fade-in shows it with 0 wait),
//   • plain MP4 reels are prefetched into the shared blob cache (reelVideoCache).
// Everything is best-effort and runs at most once per session.
import { api } from "@/lib/api";
import { prefetchAllowed, heavyPrefetchAllowed } from "@/lib/prefetchPolicy";
import { resolveMediaUrl } from "@/components/FileUpload";
import { isHlsSrc, derivePosterFromHls, preconnectVideoHost, startBandwidthSeed, isSlowNetwork, rememberBandwidth } from "@/lib/videoSource";
import { prefetchReel } from "@/lib/reelVideoCache";

let warmed = false;

const fetchText = async (url, signal) => {
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.text();
};

// The variant hls.js will start with: highest BANDWIDTH under the remembered network
// seed (see startBandwidthSeed) ; lien lent → the lowest one.
const parseVariants = (masterText) => {
  const lines = masterText.split(/\r?\n/);
  const variants = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^#EXT-X-STREAM-INF:.*BANDWIDTH=(\d+)/);
    if (m && lines[i + 1] && !lines[i + 1].startsWith("#")) variants.push({ bw: Number(m[1]), uri: lines[i + 1].trim() });
  }
  return variants.sort((a, b) => a.bw - b.bw);
};
const startVariantUrl = (masterText, masterUrl) => {
  const variants = parseVariants(masterText);
  if (!variants.length) return null;
  const seed = startBandwidthSeed();
  const fit = seed ? variants.filter((v) => v.bw < seed).pop() : null;
  return new URL((fit || variants[0]).uri, masterUrl).href;
};

// Réseau INCONNU (rien de mémorisé) : on MESURE avec le premier segment de la variante la plus basse
// (petit fichier) — durée du corps seul, hors latence — et on mémorise. Sert de point de départ à
// hls.js (Android/web : plus de segment de test) ET au lecteur natif iPhone (qui ne mesure pas).
const MEASURE_MIN_MS = 20;
const measureWithLowest = async (masterText, masterUrl, signal) => {
  const variants = parseVariants(masterText);
  if (!variants.length) return;
  const mediaUrl = new URL(variants[0].uri, masterUrl).href;
  const { init, seg } = firstSegmentUrl(await fetchText(mediaUrl, signal), mediaUrl);
  const target = seg || init;
  if (!target) return;
  const r = await fetch(target, { signal });
  if (!r.ok) return;
  const t0 = performance.now();
  const body = await r.arrayBuffer();
  const ms = Math.max(MEASURE_MIN_MS, performance.now() - t0);
  if (body.byteLength > 0) rememberBandwidth((body.byteLength * 8 * 1000) / ms);
};

// First media segment (or init map) of a media playlist.
const firstSegmentUrl = (mediaText, mediaUrl) => {
  const lines = mediaText.split(/\r?\n/);
  const init = lines.find((l) => l.startsWith("#EXT-X-MAP:"));
  const initUri = init && (init.match(/URI="([^"]+)"/) || [])[1];
  const seg = lines.find((l) => l && !l.startsWith("#"));
  return {
    init: initUri ? new URL(initUri, mediaUrl).href : null,
    seg: seg ? new URL(seg.trim(), mediaUrl).href : null,
  };
};

// Master → (mesure réseau si inconnu) → start-variant playlist → first segment (+ init map) into the HTTP cache.
const warmHlsChain = async (src, signal) => {
  const master = await fetchText(src, signal);
  if (!startBandwidthSeed() && !isSlowNetwork()) await measureWithLowest(master, src, signal);
  const mediaUrl = startVariantUrl(master, src);
  if (!mediaUrl) return;
  const media = await fetchText(mediaUrl, signal);
  const { init, seg } = firstSegmentUrl(media, mediaUrl);
  if (init) await fetch(init, { signal }).catch(() => {});
  if (seg) await fetch(seg, { signal }).catch(() => {});
};

// Fired on the FIRST touch / hover of a reel thumbnail that has no inline teaser
// (profile grid, trending strip, idle discover tiles): the manifest + first segment are
// already cached ~100-300 ms later when the tap navigates to the Reels player.
// Once per URL per session; HLS only (an MP4 prefetch would mean a full download).
const touchWarmed = new Set();
// Lot 593 — même préchauffage (manifeste + 1er segment dans le cache HTTP, sans décodeur) réutilisé pour les
// reels +2/+3 et la story suivante. Une fois par URL et par session ; les préchauffages s'enchaînent UN par UN
// (file) pour ne jamais concurrencer le reel / la story en cours de lecture ; meilleur effort, jamais bloquant.
let warmQueue = Promise.resolve();
export function warmHlsOnce(rawSrc, timeoutMs = 8000) {
  // Lot 678 — politique unique : garde-fou admin, lien 2G et économiseur de données coupent le préchauffage
  // (le cache HTTP ne prend aucun décodeur : permis même sur un téléphone à faible mémoire).
  if (!prefetchAllowed()) return false;
  const src = resolveMediaUrl(rawSrc || "");
  if (!src || !isHlsSrc(src) || touchWarmed.has(src)) return false;
  touchWarmed.add(src);
  preconnectVideoHost(src);
  warmQueue = warmQueue.then(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    return warmHlsChain(src, ctrl.signal).catch(() => {}).finally(() => clearTimeout(timer));
  });
  return true;
}
export function warmReelTouch(post) {
  if (!post?.hls_url) return;
  warmHlsOnce(post.hls_url);
}

export async function warmFirstReel() {
  // Lot 678 — même politique que warmHlsOnce ; `warmed` n'est posé que si on préchauffe vraiment (un réseau qui
  // redevient bon plus tard dans la session pourra encore en profiter).
  if (warmed || !prefetchAllowed()) return;
  warmed = true;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 12000);
  try {
    const { data } = await api.get("/reels");
    const list = data?.reels || [];
    const first = list.find((r) => r.hls_url || r.video_url);
    if (!first) return;
    const src = resolveMediaUrl(first.hls_url || first.video_url);
    preconnectVideoHost(src);
    // Pre-decode the poster so the full-screen first frame shows with zero wait.
    const poster = first.poster_url
      ? resolveMediaUrl(first.poster_url)
      : (isHlsSrc(src) ? derivePosterFromHls(src) : null);
    if (poster) { const img = new Image(); img.src = poster; }
    if (isHlsSrc(src)) {
      touchWarmed.add(src);
      await warmHlsChain(src, ctrl.signal);
    } else if (heavyPrefetchAllowed()) {
      // MP4/R2: shared blob cache — ReelCard adopts the very same blob on mount.
      // Lot 678 — un blob MP4 complet vit en mémoire : évité sur un téléphone à faible mémoire (≤ 2 Go).
      await prefetchReel(src);
    }
  } catch { /* best-effort — never disturb the Home screen */ }
  finally { clearTimeout(timer); }
}
